import { useCallback, useEffect, useState } from 'react';
import {
  UnauthenticatedError,
  api,
  type FeedStatus,
  type ModeState,
  type Settings,
  type VenueRouting,
} from './lib/api.ts';
import { useLiveStream } from './lib/useLiveStream.ts';
import { Login } from './pages/Login.tsx';
import { Opportunities } from './pages/Opportunities.tsx';
import { Balances } from './pages/Balances.tsx';
import { TradeLog } from './pages/TradeLog.tsx';
import { SettingsPage } from './pages/SettingsPage.tsx';
import { Phase0 } from './pages/Phase0.tsx';
import { Executions } from './pages/Executions.tsx';
import { Guide } from './pages/Guide.tsx';
import { ModeBanner } from './components/ModeBanner.tsx';

type Page = 'opportunities' | 'phase0' | 'transfers' | 'balances' | 'trades' | 'settings' | 'guide';

/**
 * Navigation is derived from the active mode, not hard-coded.
 *
 * The build brief's rule is that only the active method's controls are shown
 * and the other is hidden rather than deleted — so this returns a different
 * list per mode, and flipping ARBITRAGE_MODE brings the hidden UI back with no
 * code change. Phase 0 appears only while the observer is switched on.
 */
function pagesFor(mode: ModeState | null): Array<{ id: Page; label: string }> {
  const pages: Array<{ id: Page; label: string }> = [
    { id: 'opportunities', label: 'Opportunities' },
  ];
  if (mode?.pocMeasurement) pages.push({ id: 'phase0', label: 'Phase 0' });
  // Only the transfer method has multi-step runs to watch; the pre-positioned
  // one fires both legs at once and belongs in the trade log.
  if (mode?.mode === 'transfer') pages.push({ id: 'transfers', label: 'Transfers' });
  pages.push(
    { id: 'balances', label: 'Balances & feeds' },
    { id: 'trades', label: 'Trade log' },
    { id: 'settings', label: 'Settings' },
    { id: 'guide', label: 'Guide' },
  );
  return pages;
}

export function App() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [username, setUsername] = useState('');
  const [page, setPage] = useState<Page>('opportunities');
  const [settings, setSettings] = useState<Settings | null>(null);
  const [quoteCurrency, setQuoteCurrency] = useState('USDT');
  const [polledFeeds, setPolledFeeds] = useState<FeedStatus[]>([]);
  const [routing, setRouting] = useState<VenueRouting[]>([]);
  const [mode, setMode] = useState<ModeState | null>(null);

  const live = useLiveStream(authenticated === true);

  const checkAuth = useCallback(async () => {
    try {
      const me = await api.me();
      setUsername(me.username);
      setAuthenticated(true);
    } catch {
      setAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    void checkAuth();
    // Mode is unauthenticated so the banner renders on the login screen too:
    // "is this thing allowed to trade?" should be answerable before logging in.
    void api.mode().then(setMode).catch(() => undefined);
  }, [checkAuth]);

  // Settings and feed status come over REST as well as the socket: the socket
  // may not have delivered its first frame yet, and a page that renders
  // "no data" for two seconds on every load reads as broken.
  const loadStatus = useCallback(async () => {
    if (authenticated !== true) return;
    try {
      const status = await api.status();
      setSettings(status.settings);
      setQuoteCurrency(status.quoteCurrency);
      setPolledFeeds(status.feeds);
      setRouting(status.routing ?? []);
    } catch (err) {
      if (err instanceof UnauthenticatedError) setAuthenticated(false);
    }
  }, [authenticated]);

  useEffect(() => {
    void loadStatus();
    const timer = setInterval(() => void loadStatus(), 15_000);
    return () => clearInterval(timer);
  }, [loadStatus]);

  const logout = async () => {
    await api.logout().catch(() => undefined);
    setAuthenticated(false);
  };

  // The guide is public: it can be shared with a client without handing out a
  // login, and it holds nothing a stranger could use. The mode it shows comes
  // from /api/mode, which is unauthenticated for the same reason.
  if (window.location.pathname === '/guide') {
    return (
      <main className="guide-standalone">
        <Guide mode={mode} publicView />
      </main>
    );
  }

  if (authenticated === null) {
    return <div className="empty" style={{ paddingTop: 120 }}>Loading…</div>;
  }

  if (!authenticated) {
    return (
      <Login
        onSuccess={() => {
          void checkAuth();
        }}
      />
    );
  }

  const feeds = live.feeds.length > 0 ? live.feeds : polledFeeds;
  const liveVenues = feeds.filter((f) => f.state === 'live').length;

  return (
    <div className="app">
      <header className="topbar">
        <div className="brand">
          Arbitrage<span>.</span>
        </div>

        <nav className="nav">
          {pagesFor(mode).map((p) => (
            <button
              key={p.id}
              className={page === p.id ? 'active' : ''}
              onClick={() => setPage(p.id)}
            >
              {p.label}
              {p.id === 'opportunities' && live.opportunities.length > 0 && (
                <span className="green"> · {live.opportunities.length}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="topbar-right">
          <span title="venues currently streaming">
            <span className={`dot ${liveVenues === feeds.length && liveVenues > 0 ? 'live' : 'reconnecting'}`} />
            {liveVenues}/{feeds.length || '—'} feeds
          </span>
          <span className="faint">{username}</span>
          <button className="btn small" onClick={() => void logout()}>
            Sign out
          </button>
        </div>
      </header>

      <ModeBanner mode={mode} />

      <main>
        {page === 'opportunities' && (
          <Opportunities
            opportunities={live.opportunities}
            settings={settings}
            quoteCurrency={quoteCurrency}
            connected={live.connected}
            mode={mode}
          />
        )}
        {page === 'phase0' && <Phase0 />}
        {page === 'transfers' && <Executions quoteCurrency={quoteCurrency} />}
        {page === 'balances' && (
          <Balances feeds={feeds} quoteCurrency={quoteCurrency} routing={routing} />
        )}
        {page === 'trades' && <TradeLog quoteCurrency={quoteCurrency} />}
        {page === 'settings' && <SettingsPage mode={mode} />}
        {page === 'guide' && <Guide mode={mode} />}
      </main>
    </div>
  );
}
